import { BPM_MAX, BPM_MIN, MASTER_VOLUME_MAX } from "../../audio/constants";
import type { EngineActions } from "../../hooks/useAudioEngine";
import type { ParameterPath } from "../../state/parameterRoutes";
import type { ControlValue } from "../../state/controlStore";
import type { EngineSnapshot, UserControls } from "../../state/types";
import { formatDb } from "../../utils/math";
import { Slider } from "../ui/Slider";

export interface TransportControlsProps {
  snapshot: EngineSnapshot;
  controls: UserControls;
  actions: EngineActions;
  setControl: (path: ParameterPath, value: ControlValue) => void;
}

const STATUS_LABEL: Record<EngineSnapshot["status"], string> = {
  idle: "停止中（未開始）",
  starting: "開始中",
  running: "再生中",
  paused: "一時停止（位置を保持）",
  panicked: "Panic（Re-armが必要）",
  failed: "初期化失敗",
  disposed: "破棄済み",
};

export function TransportControls({
  snapshot,
  controls,
  actions,
  setControl,
}: TransportControlsProps) {
  const { status } = snapshot;
  const running = status === "running";
  const paused = status === "paused";
  const panicked = status === "panicked";

  /**
   * Resume takes ~400ms (rebuilding the queue, then waiting for already-reserved
   * audio to drain) during which the engine is still `paused` and silent.
   * Without a distinct label that reads as "nothing happened".
   *
   * Two sources, both meaning "resume in progress": the snapshot flag is
   * authoritative but only polled at 10Hz, while the action's busy flag flips
   * synchronously on click so the label changes immediately.
   */
  const resuming = snapshot.isResuming || (actions.busy && paused);
  const statusText = resuming ? "再開準備中…" : STATUS_LABEL[status];

  return (
    <section className="transport" aria-labelledby="transport-title">
      <h2 id="transport-title" className="transport__title">
        Transport
      </h2>

      {/* Status is announced as text, never by colour alone. Changing this text
          is what makes aria-live announce the resume transition. */}
      <p className="transport__status" role="status" aria-live="polite">
        {statusText}
      </p>

      {resuming && (
        <p className="transport__notice transport__notice--info">
          予約済みの音が消えるのを待っています。次の小節（bar {snapshot.resumeBar}）から再開します。
        </p>
      )}

      <div className="transport__buttons">
        <button
          type="button"
          className="button"
          onClick={actions.pause}
          disabled={!running || actions.busy}
        >
          Stop
        </button>
        <button
          type="button"
          className="button button--primary"
          onClick={actions.resume}
          disabled={!paused || actions.busy}
        >
          {actions.busy && paused ? "Resuming…" : "Resume"}
        </button>
        <button
          type="button"
          className="button"
          onClick={actions.reset}
          disabled={(!running && !paused) || actions.busy}
        >
          Reset
        </button>
        <button
          type="button"
          className="button button--danger"
          onClick={actions.panic}
          disabled={status === "idle" || status === "disposed"}
        >
          Panic
        </button>
        {panicked && (
          <button type="button" className="button" onClick={actions.arm}>
            Re-arm
          </button>
        )}
      </div>

      {panicked && (
        <p className="transport__notice" role="alert">
          Panic中です。Re-armしてからResumeで再開できます。Re-arm直後も出力は0のままです。
        </p>
      )}

      <dl className="transport__readouts">
        <div>
          <dt>BPM</dt>
          <dd>{snapshot.bpm}</dd>
        </div>
        <div>
          <dt>Bar</dt>
          <dd>{snapshot.bar}</dd>
        </div>
        <div>
          <dt>Beat</dt>
          <dd>{snapshot.beat + 1} / 4</dd>
        </div>
        <div>
          <dt>Audio</dt>
          <dd>{snapshot.diagnostics.audioContextState}</dd>
        </div>
        <div>
          <dt>Output</dt>
          <dd>
            {snapshot.diagnostics.masterMuteReason === "none"
              ? formatDb(snapshot.diagnostics.masterAppliedGain)
              : `0（${snapshot.diagnostics.masterMuteReason}）`}
          </dd>
        </div>
      </dl>

      {/* Resume is quantised to the next bar; the plan requires stating this in
          the UI. While resuming the notice above says the same thing, so this
          would just be duplicated. */}
      {!resuming && (
        <p className="transport__hint">
          Resumeは次の小節の頭（bar {snapshot.resumeBar}）から再開します。
        </p>
      )}

      <Slider
        label="BPM (manual)"
        value={controls.transport.bpmManual}
        min={BPM_MIN}
        max={BPM_MAX}
        step={1}
        unit=" BPM"
        hint="小節境界で1小節あたり最大2 BPMずつ適用されます。Auto TempoはOFFのため、他の操作でBPMは変わりません。"
        onChange={(value) => {
          setControl("transport.bpmManual", value);
        }}
      />

      <Slider
        label="Master volume"
        value={controls.sound.masterVolume}
        min={0}
        max={MASTER_VOLUME_MAX}
        step={0.005}
        displayValue={formatDb(controls.sound.masterVolume)}
        hint="一時停止中は値のみ保持し、出力は0のままです。Resumeで反映されます。"
        onChange={(value) => {
          setControl("sound.masterVolume", value);
        }}
      />

      <div className="transport__toggles">
        <label className="toggle">
          <input
            type="checkbox"
            checked={controls.sound.muted}
            onChange={(event) => {
              setControl("sound.muted", event.target.checked);
            }}
          />
          <span>Mute</span>
        </label>

        <label className="toggle">
          <input
            type="checkbox"
            checked={controls.diagnosticClick}
            onChange={(event) => {
              setControl("diagnosticClick", event.target.checked);
            }}
          />
          <span>診断用クリック（低音量）</span>
        </label>

        <label className="toggle toggle--disabled">
          <input type="checkbox" checked={controls.transport.autoTempo} disabled />
          <span>Auto Tempo（Phase 1以降）</span>
        </label>
      </div>
    </section>
  );
}
